import { useEffect, useState, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, MessageSquare, Trash2, X } from 'lucide-react';
import DeleteGroupModal from '../../components/groups/chat/DeleteGroupModal';
import GroupChatHeader from '../../components/groups/chat/GroupChatHeader';
import GroupDetailsModal from '../../components/groups/chat/GroupDetailsModal';
import GroupMessageInput from '../../components/groups/chat/GroupMessageInput';
import GroupMessageList from '../../components/groups/chat/GroupMessageList';
import RemoveGroupMemberModal from '../../components/groups/chat/RemoveGroupMemberModal';
import PhotoImage from '../../components/common/PhotoImage';
import { deleteGroup, getGroupById, removeGroupMember, uploadGroupAttachment } from '../../services/groupService';
import { Admin_GetGroupMessages, Admin_GetMessageHistory } from '../../constants/adminData';
import type { GroupChatMessage, GroupDto, GroupMemberDto, SendSelectedMembersMessagePayload } from '../../types/groups';
import Toast from '../../components/Toast/Toast';
import AccessibleDrawer from '../../components/common/AccessibleDrawer';
import { getSensitiveData } from '../../utils/secureStorage';
import { useSignalR } from '../../hooks/useSignalR';
import { useFieldLabel } from '../../hooks/useFieldLabel';

function getMemberName(member: GroupMemberDto) {
  return member.user?.name?.trim() || 'Unknown User';
}
function getMemberPhotoId(member: GroupMemberDto) {
  return member.user?.profilePhotoId ?? null;
}
function normalizeUserId(userId: unknown) {
  return String(userId ?? '').trim();
}
function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}
interface MemberSelectionListProps {
  readonly members: GroupMemberDto[];
  readonly selectedMemberIds: string[];
  readonly canRemoveMembers: boolean;
  readonly currentUserId: string;
  readonly onToggleMember: (userId: string) => void;
  readonly onRemoveMember: (member: GroupMemberDto) => void;
}
function MemberSelectionList({
  members,
  selectedMemberIds,
  canRemoveMembers,
  currentUserId,
  onToggleMember,
  onRemoveMember,
}: MemberSelectionListProps) {
  return (
<div className="space-y-2">
      {members.length > 0 ? (
        members.map((member) => {
          const memberUserId = normalizeUserId(member.userId);
          const name = getMemberName(member);
          const photoId = getMemberPhotoId(member);
          const isSelected = selectedMemberIds.includes(memberUserId);
          const isCurrentUser = memberUserId === normalizeUserId(currentUserId);
          return (
<div
              key={memberUserId}
              className={`flex items-center gap-3 rounded-lg border px-3 py-3 transition ${
                isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
>
  <div className="flex h-4 w-4 shrink-0 items-center justify-center">
  {!isCurrentUser && (
    <input
      type="checkbox"
      checked={isSelected}
      onChange={() => onToggleMember(memberUserId)}
      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      aria-label={`Select ${name}`}
    />
  )}
</div>
              {photoId ? (
<PhotoImage
                  photoId={photoId}
                  alt={name}
                  className="h-10 w-10 rounded-full object-cover"
                  fallback={
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                      {getInitials(name) || <MessageSquare className="h-4 w-4" />}
                    </span>
                  }
                />
              ) : (
<span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                  {getInitials(name) || <MessageSquare className="h-4 w-4" />}
</span>
              )}
<div className="min-w-0 flex-1">
<p className="mb-0 truncate text-sm font-semibold text-gray-900">{name}</p>
<p className="mb-0 truncate text-xs text-gray-500">{member.user?.specialtyName || 'Specialty not available'}</p>
{/* <p className="mb-0 truncate text-xs text-gray-400">{member.user?.availabilityStatus || 'Availability not available'}</p> */}
</div>
              {isSelected ? <Check className="h-4 w-4 text-blue-600" /> : null}
              {canRemoveMembers && !isCurrentUser ? (
<button
                  type="button"
                  onClick={() => onRemoveMember(member)}
                  className="inline-flex h-9 items-center justify-center gap-1 rounded-lg px-2 text-xs font-semibold text-red-600 transition hover:bg-red-50"
>
<Trash2 className="h-4 w-4" />
                  Remove
</button>
              ) : null}
</div>
          );
        })
      ) : (
<div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-600">No members found</div>
      )}
</div>
  );
}
interface SelectedMembersActionBarProps {
  readonly count: number;
  readonly canRemoveMembers: boolean;
  readonly onMessageSelected: () => void;
  readonly onRemoveSelected: () => void;
}
function SelectedMembersActionBar({ count, canRemoveMembers, onMessageSelected, onRemoveSelected }: SelectedMembersActionBarProps) {
  if (count < 1) {
    return null;
  }
  return (
<div className="sticky bottom-[69px] z-20 border-t border-blue-100 bg-blue-50 px-4 py-3 shadow-sm sm:bottom-[73px]">
<div className="mx-auto flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
<p className="mb-0 text-sm font-semibold text-blue-900">
          {count} {count === 1 ? 'Member' : 'Members'} Selected
</p>
<div className="flex flex-wrap items-center gap-2">
<button
            type="button"
            onClick={onMessageSelected}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
>
<MessageSquare className="h-4 w-4" />
            Message Selected
</button>
          {/* {canRemoveMembers ? (
<button
              type="button"
              onClick={onRemoveSelected}
              className="inline-flex h-10 items-center justify-center rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 shadow-sm transition hover:bg-red-50"
>
<Trash2 className="h-4 w-4" />
              Remove Selected
</button>
          ) : null} */}
</div>
</div>
</div>
  );
}
interface MessageSelectedMembersModalProps {
  readonly recipients: GroupMemberDto[];
  readonly onCancel: () => void;
  readonly onSend: (payload: SendSelectedMembersMessagePayload) => void;
  readonly isSending: boolean;
}
function MessageSelectedMembersModal({ recipients, onCancel, onSend, isSending }: MessageSelectedMembersModalProps) {
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const handleSend = () => {
    const trimmedMessage = message.trim();
    const recipientUserIds = recipients.map((recipient) => normalizeUserId(recipient.userId)).filter(Boolean);
    if (recipientUserIds.length === 0) {
      setError('Select at least one member.');
      return;
    }
    if (!trimmedMessage) {
      setError('Message cannot be empty.');
      return;
    }
    onSend({
      recipientUserIds,
      message: trimmedMessage,
    });
  };
  return (
<div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4 ">
<div className="w-full max-w-[600px] max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl">
<div className="w-full max-w-[95%] overflow-hidden rounded-xl bg-white shadow-2xl sm:max-w-[550px] md:max-w-[600px] max-h-[90vh]">
<div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
<h2 className="mb-0 text-lg font-bold text-gray-900">Send Message</h2>
<button
            type="button"
            onClick={onCancel}
            disabled={isSending}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
            aria-label="Close send message"
>
<X className="h-5 w-5" />
</button>
</div>
<div className="overflow-y-auto max-h-[60vh] px-5 py-5">
<div className="space-y-4">
<div>
<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Recipients</p>
<div className="flex flex-wrap gap-2">
                {recipients.map((recipient) => (
<span key={normalizeUserId(recipient.userId)} className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-100">
                    {getMemberName(recipient)}
</span>
                ))}
</div>
</div>
<div>
<label className="mb-2 block text-sm font-semibold text-gray-800" htmlFor="selectedMembersMessage">
                Message
</label>
<textarea
                id="selectedMembersMessage"
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value);
                  setError('');
                }}
                placeholder="Type your message..."
                rows={5}
                disabled={isSending}
                className="w-full resize-none rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50"
              />
              {error ? <p className="mb-0 mt-1 text-xs text-red-600">{error}</p> : null}
</div>
</div>
</div>
<div className="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:justify-end sm:items-center">
<button
            type="button"
            onClick={onCancel}
            disabled={isSending}
            className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 sm:w-auto"
>
            Cancel
</button>
<button
            type="button"
            onClick={handleSend}
            disabled={isSending}
            className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50 sm:w-auto"
>
            {isSending ? 'Sending...' : 'Send'}
</button>
</div>
</div>
</div>
</div>
  );
}
export default function GroupChatPageEnhanced() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { groupId } = useParams<{ groupId: string }>();
  const numericGroupId = Number(groupId);
  const scrollToMessageId: number | undefined = (location.state as { scrollToMessageId?: number } | null)?.scrollToMessageId;
  // Message and UI state
  const [messages, setMessages] = useState<GroupChatMessage[]>([]);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);
  // Restored: selection and member broadcast state
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [isMemberDrawerOpen, setIsMemberDrawerOpen] = useState(false);
  const [isMessageSelectedOpen, setIsMessageSelectedOpen] = useState(false);
  const [isRemoveSelectedOpen, setIsRemoveSelectedOpen] = useState(false);
  const [isRemovingSelected, setIsRemovingSelected] = useState(false);
  const [currentUserId, setCurrentUserId] = useState('');
  const currentUserIdRef = useRef('');
  const [memberPendingRemoval, setMemberPendingRemoval] = useState<GroupMemberDto | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);
  const processedMessagesRef = useRef<Set<string>>(new Set());
  const conversationIdRef = useRef<number | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listLabel = useFieldLabel('MENU_LIST', 'List');
  useEffect(() => {
  getSensitiveData('userId').then((userId) => {
    const id = userId || '';

    setCurrentUserId(id);
    currentUserIdRef.current = id;

  });
}, []);
  const {
    data: group,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['group-details', numericGroupId],
    queryFn: () => getGroupById(numericGroupId),
    enabled: Number.isFinite(numericGroupId) && numericGroupId > 0,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
  // Store conversation ID
  useEffect(() => {
    if (group) {
      conversationIdRef.current = (group as any).conversationId || null;
      // Clear deduplication cache when group changes
      processedMessagesRef.current.clear();
    }
  }, [group?.groupId]);
  // Handle incoming group messages
  const handleReceiveGroupMessage = useCallback((message: any) => {

    if (!message?.messageId || !message?.groupId) {
      console.warn('⚠️ Invalid message: missing messageId or groupId', message);
      return;
    }
    // Only process messages for this group
    if (message.groupId !== numericGroupId) {
      return;
    }
    const messageKey = `${message.groupId}-${message.messageId}`;
    if (processedMessagesRef.current.has(messageKey)) {
      return;
    }
    processedMessagesRef.current.add(messageKey);
    //const isSentByCurrentUser = message.senderId?.toLowerCase() === currentUserId.toLowerCase();
    const normalizeId = (id?: string) =>  (id || '').replace(/[{}]/g, '').trim().toLowerCase();
 
    const isSentByCurrentUser = normalizeId(message.senderId) === normalizeId(currentUserIdRef.current);
    
    const newMessage: GroupChatMessage = {
      messageId: message.messageId,
      senderId: message.senderId,
      senderName: message.senderName || 'Unknown User',
      senderPhoto: message.senderPhoto,
      createdAt: message.createdAt || new Date().toISOString(),
      messageText: message.messageText || message.messageContent,
      isOwnMessage: isSentByCurrentUser,
      messageType: message.messageType,
      attachmentUrl: message.attachmentUrl,
      conversationId: message.conversationId,
    };
    setMessages((prev) => {
      // Check for duplicates
      if (prev.some(m => m.messageId === newMessage.messageId)) {
        return prev;
      }
      return [...prev, newMessage];
    });
  }, [numericGroupId, currentUserId]);
  // Handle typing indicator
  const handleUserTyping = useCallback((payload: any) => {
    const userId = payload?.userId || payload?.UserId;
    const isTyping = payload?.isTyping ?? false;
    const conversationId = payload?.conversationId;
    // Only process typing for this conversation
    if (conversationId !== conversationIdRef.current) {
      return;
    }
    // Don't show typing indicator for current user
    if (userId?.toLowerCase() === currentUserId.toLowerCase()) {
      return;
    }
    setTypingUsers((prev) => {
      const newSet = new Set(prev);
      if (isTyping) {
        newSet.add(userId);
      } else {
        newSet.delete(userId);
      }
      return newSet;
    });
  }, [currentUserId]);
  // Handle read receipts
  const handleMessagesRead = useCallback((payload: any) => {
    const messageIds = payload?.MessageIds || payload?.messageIds || payload;
    if (!Array.isArray(messageIds)) return;
    setMessages((prev) =>
      prev.map((m) =>
        messageIds.includes(m.messageId)
          ? { ...m, deliveryStatus: 2 }
          : m
      )
    );
  }, []);
  // SignalR connection
  const {
  connectionStatus,
  isConnected,
  joinGroupChat,
  leaveGroupChat,
  sendGroupMessage,
  sendMessage,
  sendTypingIndicator,
} = useSignalR({
    onReceiveGroupMessage: handleReceiveGroupMessage,
    onUserTyping: handleUserTyping,
    onMessagesRead: handleMessagesRead,
    autoConnect: true,
  });
  // Note: Group joining not required - backend auto-handles via conversationId context
  // The group context is established when messages are sent with groupId + conversationId
  // // Load chat history when group loads
  // useEffect(() => {
  //   if (!group) {
  //     return;
  //   }
  //   const loadChatHistory = async () => {
  //     try {
  //       // In a real implementation, you might have a separate API call to load chat history
  //       const chatMessages: GroupChatMessage[] = [];
  //       setMessages(chatMessages);
  //     } catch (error) {
  //       console.error('Failed to load chat history:', error);
  //       setMessages([]);
  //     }
  //   };
  //   loadChatHistory();
  // }, [group?.groupId]);

    useEffect(() => {
      if (!isConnected || !numericGroupId)
        return;

      joinGroupChat(numericGroupId)
        .then(() => {
        })
        .catch(console.error);

      return () => {
        leaveGroupChat(numericGroupId)
          .catch(console.error);
      };
    }, [
      isConnected,
      numericGroupId,
      joinGroupChat,
      leaveGroupChat
    ]);


  useEffect(() => {
  if (!group || !numericGroupId) {
    return;
  }
    const loadChatHistory = async () => {
      try {
        const response = await Admin_GetGroupMessages(
          numericGroupId,
          1,
          50
        );
        const apiMessages = response?.data?.messages ?? [];
        const normalizeId = (id?: string) =>
          (id || '')
            .replace(/[{}]/g, '')
            .trim()
            .toLowerCase();

        const mappedMessages: GroupChatMessage[] =
          apiMessages.map((m: any) => ({
            messageId: m.messageId,
            senderId: m.senderId,
            senderName: m.senderName,
            conversationId: m.conversationId,
            createdAt: m.sentAt,
            messageText: m.messageContent,
            messageType: m.messageType,
            attachmentUrl: m.attachmentUrl,
            isOwnMessage: normalizeId(m.senderId) === normalizeId(currentUserId),
          }));

        // Pre-populate deduplication cache with API messages to avoid duplicate real-time additions
        apiMessages.forEach((m: any) => {
          const messageKey = `${numericGroupId}-${m.messageId}`;
          processedMessagesRef.current.add(messageKey);
        });
        setMessages(mappedMessages);
      } catch (error) {
        console.error(
          "LOAD CHAT HISTORY ERROR",
          error
        );
      }
    };
    loadChatHistory();
  }, [
    group?.groupId,
    numericGroupId,
    currentUserId,
  ]);

  // Reset processed messages when component unmounts (cleanup on navigation away)
  useEffect(() => {
    return () => {
      processedMessagesRef.current.clear();
    };
  }, []);
  const handleSendMessage = async (messageText: string) => {
    if (!group || !messageText.trim() || !isConnected) {
      if (!isConnected) {
        console.warn('⚠️ SignalR not connected. Current status:', connectionStatus);
        setToast({ message: 'Connection lost. Please wait...', type: 'error' });
      }
      return;
    }
    const conversationId = conversationIdRef.current;
    if (!conversationId) {
      console.warn('⚠️ Missing conversation ID for group', numericGroupId);
      setToast({ message: 'Unable to send message. Missing conversation ID.', type: 'error' });
      return;
    }

    //const tempMessageId = Date.now();
    try {
      //// Optimistic UI update
      // const tempMessage: GroupChatMessage = {
      //   messageId: tempMessageId,
      //   senderId: currentUserId,
      //   senderName: 'You',
      //   senderPhoto: undefined,
      //   createdAt: new Date().toISOString(),
      //   messageText: messageText.trim(),
      //   isOwnMessage: true,
      //   messageType: 'Text',
      //   conversationId,
      // };
      // setMessages((prev) => [...prev, tempMessage]);


      //// Send via SignalR
      await sendGroupMessage({
        groupId: numericGroupId,
        conversationId,
        messageContent: messageText.trim(),
        messageType: 'Text',
      });
      //// The actual message will come back via SignalR broadcast
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unable to send message.';
      setToast({ message: errorMessage, type: 'error' });
      //// Remove optimistic message on error
     // setMessages((prev) => prev.filter((m) => m.messageId !== tempMessageId));
    }
  };
  const handleTyping = (isTyping: boolean) => {
    if (!isConnected || !conversationIdRef.current) {
      return;
    }
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    sendTypingIndicator(conversationIdRef.current, isTyping).catch((error) => {
      console.error('Failed to send typing indicator:', error);
    });
    // Auto-stop typing after 3 seconds
    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        if (conversationIdRef.current) {
          sendTypingIndicator(conversationIdRef.current, false).catch(() => {});
        }
      }, 3000);
    }
  };
  const handleSelectFile = async (file: File) => {
    if (!group || !isConnected) {
      if (!isConnected) {
        setToast({ message: 'Connection lost. Please wait...', type: 'error' });
      }
      return;
    }

    const conversationId = conversationIdRef.current;
    if (!conversationId) {
      setToast({ message: 'Unable to send attachment. Missing conversation ID.', type: 'error' });
      return;
    }

    try {
      const uploaded = await uploadGroupAttachment(file);

      await sendGroupMessage({
        groupId: numericGroupId,
        conversationId,
        messageContent: file.name,
        messageType: 'File',
        attachmentUrl: uploaded.attachmentUrl,
      });
    } catch (error) {
      console.error('Failed to upload attachment:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unable to upload attachment.';
      setToast({ message: errorMessage, type: 'error' });
    }
  };
  const handleDelete = async () => {
    try {
      setIsDeletingGroup(true);
      await deleteGroup(numericGroupId);
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
      await queryClient.refetchQueries({ queryKey: ['groups'] });
      setToast({ message: `${listLabel} deleted successfully.`, type: 'success' });
      setIsDeleteOpen(false);
      globalThis.setTimeout(() => navigate('/groups'), 700);
    } catch (error) {
      console.error('Delete group failed', error);
      setToast({ message: `Unable to delete ${listLabel.toLowerCase()}. Please try again.`, type: 'error' });
    } finally {
      setIsDeletingGroup(false);
    }
  };
  // Restored: Member selection helper
  const toggleMemberSelection = (userId: string) => {
    const normalizedUserId = normalizeUserId(userId);
    if (!normalizedUserId) {
      return;
    }
    setSelectedMemberIds((current) =>
      current.includes(normalizedUserId)
        ? current.filter((selectedUserId) => selectedUserId !== normalizedUserId)
        : [...current, normalizedUserId]
    );
  };
  // Restored: Open selected members messaging flow
  const handleMessageSelected = () => {
    if (selectedMembers.length < 1) {
      setToast({ message: 'Select at least one member.', type: 'warning' });
      return;
    }
    setIsMessageSelectedOpen(true);
  };
  const handleRemoveSelected = () => {
    if (selectedMembers.length < 1) {
      setToast({ message: 'Select at least one member to remove.', type: 'warning' });
      return;
    }
    setIsRemoveSelectedOpen(true);
  };
  const handleConfirmRemoveSelected = async () => {
    if (!selectedMembers.length) {
      return;
    }
    setIsRemovingSelected(true);
    try {
      for (const member of selectedMembers) {
        await removeGroupMember(numericGroupId, normalizeUserId(member.userId));
      }
      setSelectedMemberIds([]);
      setIsRemoveSelectedOpen(false);
      setToast({ message: 'Selected members removed successfully.', type: 'success' });
      await refetch();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unable to remove selected members. Please try again.';
      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setIsRemovingSelected(false);
    }
  };
  // Send selected members through the existing one-to-one SignalR flow.
  const handleSendSelectedMembersMessage = async (payload: SendSelectedMembersMessagePayload) => {
    const trimmedMessage = payload.message.trim();
    const loggedInUserId = normalizeUserId(currentUserId || (await getSensitiveData('userId')));
    const recipients = Array.from(
      new Set(
        payload.recipientUserIds
          .map(normalizeUserId)
          .filter((id) => id && id.toLowerCase() !== loggedInUserId.toLowerCase())
      )
    );

    if (recipients.length === 0) {
      setToast({ message: 'Select at least one member.', type: 'warning' });
      return;
    }

    if (!trimmedMessage) {
      setToast({ message: 'Message cannot be empty.', type: 'warning' });
      return;
    }

    if (!isConnected) {
      setToast({ message: 'Connection lost. Please wait...', type: 'error' });
      return;
    }
    setIsSendingBroadcast(true);
    try {
      const authToken = await getSensitiveData('authToken');
      if (!authToken) {
        throw new Error('Unable to send message. Please sign in again.');
      }

      for (const receiverId of recipients) {
        const historyResponse = await Admin_GetMessageHistory(receiverId, authToken);
        const conversationId = Number(historyResponse?.data?.conversationId);

        if (!historyResponse?.success || !Number.isFinite(conversationId) || conversationId <= 0) {
          throw new Error('Unable to create conversation for selected member.');
        }

        await sendMessage({
          receiverId,
          conversationId,
          messageContent: trimmedMessage,
        });
      }
      setToast({ message: 'Message sent successfully.', type: 'success' });
      setSelectedMemberIds([]);
      setIsMessageSelectedOpen(false);
      setIsDetailsOpen(false);
      setIsMemberDrawerOpen(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message.';
      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setIsSendingBroadcast(false);
    }
  };
  // Restored: Member removal handler with group refresh and selection cleanup
  const handleMemberRemoved = async () => {
    if (!memberPendingRemoval) {
      return;
    }
    const removedUserId = normalizeUserId(memberPendingRemoval.userId);
    queryClient.setQueryData<GroupDto>(['group-details', numericGroupId], (currentGroup) => {
      if (!currentGroup) {
        return currentGroup;
      }
      return {
        ...currentGroup,
        members: currentGroup.members?.filter((member) => normalizeUserId(member.userId) !== removedUserId) ?? [],
      };
    });
    setSelectedMemberIds((current) => current.filter((userId) => userId !== removedUserId));
    setMemberPendingRemoval(null);
    setToast({ message: 'Member removed successfully.', type: 'success' });
    await refetch();
  };
  // Restored: Member removal error feedback
  const handleRemoveMemberError = () => {
    setToast({ message: 'Unable to remove member. Please try again.', type: 'error' });
  };
  if (isLoading) {
    return (
<div className="flex h-[calc(100vh-8rem)] min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
<div className="border-b border-gray-200 px-4 py-4 sm:px-6">
<div className="flex animate-pulse items-center gap-3">
<div className="h-12 w-12 rounded-lg bg-gray-200" />
<div className="space-y-2">
<div className="h-5 w-48 rounded bg-gray-200" />
<div className="h-4 w-32 rounded bg-gray-100" />
</div>
</div>
</div>
<div className="flex-1 bg-gray-50" />
</div>
    );
  }
  if (isError || !group) {
    return (
<div className="flex min-h-[360px] flex-col items-center justify-center rounded-lg border border-gray-200 bg-white px-6 py-10 text-center shadow-sm">
<h2 className="mb-2 text-xl font-bold text-gray-900">Unable to load groups.</h2>
<button
          type="button"
          onClick={() => refetch()}
          className="mt-4 inline-flex h-10 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
>
          Retry
</button>
</div>
    );
  }
  const members = group.members ?? [];
  const selectedMembers = members.filter((member) => selectedMemberIds.includes(normalizeUserId(member.userId)));
  const sortedMessages = [...messages].sort((first, second) => new Date(first.createdAt).getTime() - new Date(second.createdAt).getTime());
  const currentMember = members.find((member) => normalizeUserId(member.userId) === normalizeUserId(currentUserId));
  const canRemoveMembers =
    Boolean(currentUserId && group.createdBy === currentUserId) || Boolean(group.isAdmin) || Boolean(currentMember?.isAdmin);
  // Get typing users names
  const typingUserNames = Array.from(typingUsers)
    .map(userId => {
      const member = members.find(m => m.userId === userId);
      return member ? getMemberName(member) : null;
    })
    .filter(Boolean);
  const typingStatusSuffix = typingUserNames.length > 2 ? ' and others' : '';
  const typingStatusText = typingUserNames.length === 1
    ? `${typingUserNames[0]} is typing...`
    : `${typingUserNames.slice(0, 2).join(', ')}${typingStatusSuffix} are typing...`;
  return (
<div className="space-y-3">
<button
  type="button"
  onClick={() => navigate('/groups')}
  className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
>
  <ArrowLeft className="h-4 w-4" />
  Back
</button>
<div className="flex h-[calc(100vh-11rem)] min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
<div className="flex min-w-0 flex-1 flex-col">
<GroupChatHeader
          group={group}
          onViewDetails={() => setIsDetailsOpen(true)}
          onDeleteGroup={() => setIsDeleteOpen(true)}
          onOpenMembers={() => setIsMemberDrawerOpen(true)}
          showDeleteOption={Boolean(currentUserId && group.createdBy === currentUserId)}
        />
        {/* Connection status indicator */}
        {connectionStatus !== 'connected' && (
<div className={`px-4 py-2 text-center text-sm ${
            connectionStatus === 'reconnecting' ? 'bg-yellow-50 text-yellow-700' : 'bg-green-50 text-green-700'
          }`}>
            {connectionStatus === 'reconnecting' ? 'Reconnecting...' : 'Connecting'}
</div>
        )}
      
<GroupMessageList messages={sortedMessages} scrollToMessageId={scrollToMessageId} />
        {/* Typing indicator */}
        {typingUserNames.length > 0 && (
<div className="px-4 py-2 text-sm text-gray-500 italic">
            {typingStatusText}
</div>
        )}
        {/* Restored: Selected members action bar for messaging highlighted members */}
<SelectedMembersActionBar
          count={selectedMemberIds.length}
          canRemoveMembers={canRemoveMembers}
          onMessageSelected={handleMessageSelected}
          onRemoveSelected={handleRemoveSelected}
        />
<GroupMessageInput 
          onSendMessage={handleSendMessage} 
          onSelectFile={handleSelectFile}
          onTyping={handleTyping}
          disabled={!isConnected}
        />
</div>
      {/* Restored: Desktop member sidebar with selection and removal support */}
<aside className="hidden w-80 flex-shrink-0 border-l border-gray-200 bg-white p-4 lg:block">
<div className="mb-4">
<h2 className="mb-1 text-sm font-bold text-gray-900">Members</h2>
<p className="mb-0 text-xs text-gray-500">Select members to send a focused message.</p>
</div>
<div className="max-h-[calc(100vh-13rem)] overflow-y-auto pr-1">
<MemberSelectionList
            members={members}
            selectedMemberIds={selectedMemberIds}
            canRemoveMembers={canRemoveMembers}
            currentUserId={currentUserId}
            onToggleMember={toggleMemberSelection}
            onRemoveMember={setMemberPendingRemoval}
          />
</div>
</aside>
      {/* Restored: Mobile member drawer with slide-in member list and selection */}
      {isMemberDrawerOpen ? (
<AccessibleDrawer
  isOpen={isMemberDrawerOpen}
  onClose={() => setIsMemberDrawerOpen(false)}
  ariaLabel="Group members"
  panelClassName="max-w-sm lg:hidden"
>
<div className="flex items-center justify-between border-b border-gray-200 px-4 py-4">
<div>
<h2 className="mb-1 text-base font-bold text-gray-900">Members</h2>
<p className="mb-0 text-xs text-gray-500">Select members to message.</p>
</div>
<button
                type="button"
                onClick={() => setIsMemberDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                aria-label="Close members drawer"
                data-dialog-initial-focus
>
<X className="h-5 w-5" />
</button>
</div>
<div className="min-h-0 flex-1 overflow-y-auto p-4">
<MemberSelectionList
                members={members}
                selectedMemberIds={selectedMemberIds}
                canRemoveMembers={canRemoveMembers}
                currentUserId={currentUserId}
                onToggleMember={toggleMemberSelection}
                onRemoveMember={setMemberPendingRemoval}
              />
</div>
</AccessibleDrawer>
      ) : null}
      {/* Restored: Group details modal with member list, selection, and actions */}
      {isDetailsOpen ? (
<GroupDetailsModal
          group={group}
          selectedMemberIds={selectedMemberIds}
          onToggleMember={toggleMemberSelection}
          onMessageSelected={handleMessageSelected}
          canRemoveMembers={canRemoveMembers}
          currentUserId={currentUserId}
          onRemoveMember={setMemberPendingRemoval}
          onClose={() => setIsDetailsOpen(false)}
        />
      ) : null}
      {/* Restored: Message selected members modal for broadcasting to chosen recipients */}
      {isMessageSelectedOpen ? (
<MessageSelectedMembersModal
          recipients={selectedMembers}
          onCancel={() => setIsMessageSelectedOpen(false)}
          onSend={handleSendSelectedMembersMessage}
          isSending={isSendingBroadcast}
        />
      ) : null}
      {isRemoveSelectedOpen ? (
<dialog open className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4 py-6">
<div className="w-full max-w-[95%] rounded-lg bg-white p-4 shadow-xl sm:max-w-[550px] md:max-w-[600px]">
<div className="flex items-center justify-between border-b border-gray-200 px-4 pb-3">
<h2 className="text-lg font-bold text-gray-900">Remove Members</h2>
<button
                type="button"
                onClick={() => setIsRemoveSelectedOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                aria-label="Close remove selected members"
>
<X className="h-5 w-5" />
</button>
</div>
<div className="space-y-4 px-4 py-4">
<p className="text-sm text-gray-700">Are you sure you want to remove these members?</p>
<ul className="space-y-2 text-sm text-gray-800">
                {selectedMembers.map((member) => (
<li key={member.userId} className="list-disc pl-5">
                    {getMemberName(member)}
</li>
                ))}
</ul>
</div>
<div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-4 sm:flex-row sm:justify-end">
<button
                type="button"
                onClick={() => setIsRemoveSelectedOpen(false)}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-gray-200 px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
>
                Cancel
</button>
<button
                type="button"
                onClick={handleConfirmRemoveSelected}
                disabled={isRemovingSelected}
                className="inline-flex h-10 items-center justify-center rounded-lg bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
>
                {isRemovingSelected ? 'Removing...' : 'Remove'}
</button>
</div>
</div>
</dialog>
      ) : null}
      {isDeleteOpen ? (
<DeleteGroupModal
          isDeleting={isDeletingGroup}
          onCancel={() => {
            if (!isDeletingGroup) {
              setIsDeleteOpen(false);
            }
          }}
          onDelete={handleDelete}
        />
      ) : null}
<RemoveGroupMemberModal
        open={Boolean(memberPendingRemoval)}
        groupId={numericGroupId}
        userId={memberPendingRemoval?.userId || ''}
        memberName={memberPendingRemoval ? getMemberName(memberPendingRemoval) : ''}
        onClose={() => setMemberPendingRemoval(null)}
        onRemoved={handleMemberRemoved}
        onError={handleRemoveMemberError}
      />
      {toast ? <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} /> : null}
</div>
</div>
  );
}
